import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useAuth } from "../hooks/useAuth"
import PromoBanner from "./PromoBanner"
import { fetchCommunityPlacesFresh } from "../utilities/communities"
import { pinnedWatchlistMembers, watchlistRow } from "../utilities/watchlist"
import { colorCode } from "../utilities/helpers"
import { FLAGS_API_URL } from "../const/paths"
import { COMMUNITY_POLL_INTERVAL_MS } from "../const/numbers"
import "./DashboardWatchlistCard.css"

const NO_SCORE_COLOR = "#dee2e6"

function WatchlistAvatar({ row }) {
    const ringColor =
        row.score == null ? NO_SCORE_COLOR : colorCode(row.score, "hex")

    // Places show their country's flag; people their photo, else initials.
    if (row.type === "place") {
        return (
            <span
                className="watchlist-avatar"
                style={{ borderColor: ringColor }}
                aria-hidden="true"
            >
                {row.countryCode && (
                    <img
                        src={`${FLAGS_API_URL}/${row.countryCode}.svg`}
                        alt=""
                    />
                )}
            </span>
        )
    }

    return (
        <span
            className="watchlist-avatar watchlist-avatar-person"
            style={{ borderColor: ringColor }}
            aria-hidden="true"
        >
            {row.pictureUrl ? (
                <img src={row.pictureUrl} alt="" />
            ) : (
                <span>{row.initials}</span>
            )}
        </span>
    )
}

function WatchlistRowBody({ row }) {
    return (
        <>
            <WatchlistAvatar row={row} />
            <span className="watchlist-text">
                <span className="watchlist-name">{row.name}</span>
                {row.detail && (
                    <span className="watchlist-detail">
                        {row.detailCountryCode && (
                            <img
                                className="watchlist-detail-flag"
                                src={`${FLAGS_API_URL}/${row.detailCountryCode}.svg`}
                                alt=""
                            />
                        )}
                        {row.detail}
                    </span>
                )}
            </span>
        </>
    )
}

/**
 * The signed-in user's watchlist on the home dashboard: their pinned people and
 * places across all communities, each with a risk-coloured ring, and a tap
 * through to that place's safety profile. Modeled on the Android Dashboard's
 * Watchlist card. Uses the same cached community data as the community page,
 * refetching once it is older than the community list's own refresh interval.
 */
export default function DashboardWatchlistCard() {
    const isLoggedIn = useAuth()
    const [communities, setCommunities] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    // The load in progress, shared rather than skipped: if the effect re-runs
    // mid-load (React StrictMode mounts twice in development; the effect
    // re-subscribes), the new run waits for the same request and uses its
    // result. Skipping it left the spinner running, because the first run's
    // result is discarded once that run is cleaned up.
    const inFlight = useRef(null)

    useEffect(() => {
        if (!isLoggedIn) return

        let cancelled = false

        const load = async () => {
            let userId = null
            try {
                userId = JSON.parse(
                    localStorage.getItem("chaperon_user_data") || "null",
                )?.id
            } catch {
                userId = null
            }
            if (!userId) {
                setIsLoading(false)
                return
            }

            if (!inFlight.current) {
                inFlight.current = fetchCommunityPlacesFresh(
                    userId,
                    COMMUNITY_POLL_INTERVAL_MS,
                ).finally(() => {
                    inFlight.current = null
                })
            }
            try {
                const loaded = await inFlight.current
                if (!cancelled) setCommunities(loaded)
            } catch (error) {
                console.error("Error loading the watchlist:", error)
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }

        // Catch up when the tab is shown again, as Android does on resume;
        // the age check makes this a no-op unless the data is stale.
        const onVisible = () => {
            if (document.visibilityState === "visible") load()
        }

        load()
        document.addEventListener("visibilitychange", onVisible)
        return () => {
            cancelled = true
            document.removeEventListener("visibilitychange", onVisible)
        }
    }, [isLoggedIn])

    const rows = useMemo(
        () => pinnedWatchlistMembers(communities).map(watchlistRow),
        [communities],
    )

    if (!isLoggedIn) return null

    // Nothing pinned yet: invite the user to build a watchlist instead of
    // showing an empty card.
    if (!isLoading && rows.length === 0) {
        return (
            <PromoBanner
                icon="bi-pin-angle"
                title="Make your own watchlist..."
                to="/community"
            >
                Pin the people and places you care about most and see them
                all here at a glance: where everyone is right now and how
                safe each place is, without digging through your
                communities.
            </PromoBanner>
        )
    }

    return (
        <section
            className="dashboard-watchlist text-start"
            aria-labelledby="dashboard-watchlist-title"
        >
            <h2 id="dashboard-watchlist-title" className="watchlist-label">
                Watchlist
            </h2>
            <div className="watchlist-card">
                {isLoading ? (
                    <div className="watchlist-empty text-center" role="status">
                        <span
                            className="spinner-border spinner-border-sm"
                            aria-label="Loading your watchlist"
                        ></span>
                    </div>
                ) : (
                    <ul className="watchlist-list">
                        {rows.map((row) => (
                            <li key={row.id}>
                                {row.slug ? (
                                    <Link
                                        className="watchlist-row"
                                        to={`/analysis/${row.slug}`}
                                    >
                                        <WatchlistRowBody row={row} />
                                    </Link>
                                ) : (
                                    <div className="watchlist-row">
                                        <WatchlistRowBody row={row} />
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                <div className="dashboard-card-footer">
                    <Link to="/community" className="dashboard-card-footer-button">
                        Community
                        <i className="bi bi-chevron-right ms-1"></i>
                    </Link>
                </div>
            </div>
        </section>
    )
}
